import { Box } from '@chakra-ui/react';
import { formatUnreadCount } from '@/utils/unreadCount';

type Props = {
  /** Unread messages in this thread. Nothing renders at zero. */
  count: number;
  /** Muted threads still count, they just do not shout about it. */
  muted?: boolean;
  /** Absolute placement, for the corner of a department card. */
  position?: 'static' | 'corner';
};

/**
 * How many messages are waiting in this thread.
 *
 * A count, never a bare dot: a dot cannot tell one missed message from forty, which with
 * department chats in the mix is the difference between "later" and "now". The rest of
 * the row still carries the softer signal -- bold title, darker preview -- from
 * ChatListItem.unread, so a thread reads as unread even in the moment before
 * get_chat_unread_counts answers.
 */
export function UnreadBadge({ count, muted = false, position = 'static' }: Props) {
  if (count <= 0) return null;

  return (
    <Box
      minW="20px"
      h="20px"
      px={1.5}
      borderRadius="full"
      bg={muted ? 'volyx.mediumGray' : 'volyx.action'}
      color="white"
      fontSize="11px"
      fontWeight="bold"
      lineHeight="20px"
      textAlign="center"
      flexShrink={0}
      fontVariantNumeric="tabular-nums"
      aria-label={`${count} unread`}
      {...(position === 'corner'
        ? { position: 'absolute' as const, top: 2.5, right: 2.5 }
        : {})}
    >
      {formatUnreadCount(count)}
    </Box>
  );
}
